import { SvgOptimizer } from '@manomano-internal/tamagoshi-icons/esm/SvgOptimizer';
import { useRouter } from 'next/router';
import React, { FC, PropsWithChildren, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Provider } from 'urql';

import { useDynamicReducer } from '@/core/applicationState/hooks/useDynamicReducer';
import { useDynamicSaga } from '@/core/applicationState/hooks/useDynamicSaga';
import { BreakpointProvider } from '@/core/tamagoshi/MediaQuery/MediaQuery';
import { KlarnaScript } from '@/customerFinanceOperations/KlarnaScript/KlarnaScript';
import { client } from '@/domains/core/graphqlClient/queryClient';
import { bulkPurchaseReducer } from '@/domains/productDiscovery/ProductPage/common/modules/BulkPurchase/ducks/BulkPurchase.reducer';
import { reducer as eligibilityCouponReducer } from '@/loyalty/modules/CouponEligibility/eligibilityCoupons.reducer';
import { rootDeliverySaga } from '@/productDiscovery/ProductPage/common/modules/Delivery/delivery.ducks';
import { PRODUCT_PAGE_INIT } from '@/productDiscovery/ProductPage/detail.actions';
import { useAddToProductHistory } from '@/productDiscovery/ProductPage/hooks/useAddToProductHistory';
import { useAlgoliaCVRTracking } from '@/productDiscovery/ProductPage/hooks/useAlgoliaCVRTracking';
import { useDetail } from '@/productDiscovery/ProductPage/hooks/useDetail';
import { usePlaylistReconciliationA2CTracking } from '@/productDiscovery/ProductPage/hooks/usePlaylistReconciliationA2CTracking';
import { SEO } from '@/productDiscovery/ProductPage/modules/SEO/SEO';
import productPageReducer from '@/productDiscovery/ProductPage/reducer';
import { trackingSaga } from '@/productDiscovery/ProductPage/tracking.saga';
import recommendationReducer from '@/productDiscovery/Recommendation/modules/ProductRecommendation/ProductRecommendation.reducer';

// TODO: PP-1300 remove custom breakpoints and BreakpointProvider
// since year 2020 there is a pending task to stop using breakpointsProvider
// https://manomano.atlassian.net/browse/PP-1300
const queries = {
  xs: `screen and (max-width: 419px)`,
  sm: `screen and (min-width: 420px) and (max-width: 767px)`,
  md: `screen and (min-width: 768px) and (max-width: 1023px)`,
  lg: `screen and (min-width: 1024px) and (max-width: 1439px)`,
  xl: `screen and (min-width: 1440px)`,
  mobile: 'screen and (max-width: 420px)',
  phablet: 'screen and (max-width: 600px)',
  tablet: 'screen and (min-width: 421px) and (max-width: 767px)',
};

export const ProductPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const { productId } = useDetail();
  const { events } = useRouter();

  useDynamicReducer('productDiscovery.productPage', productPageReducer);
  useDynamicReducer(
    `productDiscovery.productRecommendation`,
    recommendationReducer,
  );
  useDynamicReducer(
    'proB2BAnimation.eligibilityCoupon',
    eligibilityCouponReducer,
  );
  useDynamicReducer('productDiscovery.bulkPurchase', bulkPurchaseReducer);
  useDynamicSaga('rootDeliverySaga', rootDeliverySaga);
  useDynamicSaga('trackingProductPage', trackingSaga);
  useAlgoliaCVRTracking();
  usePlaylistReconciliationA2CTracking();
  useAddToProductHistory(productId);

  const dispatch = useDispatch();

  // when navigating between product pages, we need to trigger the init action the state (navigating between pages)
  useEffect(() => {
    const handleRouteChange = () => dispatch({ type: PRODUCT_PAGE_INIT });
    // router event routeChangeComplete does not trigger on new tab or reload
    events.on('routeChangeComplete', handleRouteChange);
    return () => {
      events.off('routeChangeComplete', handleRouteChange);
    };
  }, [events, dispatch]);

  // we must trigger the init action when the component is mounted ( first render or new tab or reload)
  useEffect(() => {
    dispatch({ type: PRODUCT_PAGE_INIT });
  }, [dispatch]);

  return (
    <>
      <KlarnaScript />
      <SEO />
      <SvgOptimizer>
        <Provider value={client}>
          <BreakpointProvider queries={queries}>{children}</BreakpointProvider>
        </Provider>
      </SvgOptimizer>
    </>
  );
};
